import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { Home, Map as MapIcon, QrCode, Calendar, User, Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/language-switcher";

export const Route = createFileRoute("/app")({
  head: () => ({ meta: [{ title: "Member App — Jain Gaurav Yatra" }] }),
  component: AppShell,
});

function AppShell() {
  const loc = useLocation();
  const { t } = useI18n();

  const TABS = [
    { to: "/app", label: t("home"), icon: Home, exact: true },
    { to: "/app/temples", label: t("temples"), icon: MapIcon },
    { to: "/app/scan", label: t("scanQr"), icon: QrCode },
    { to: "/app/bookings", label: t("bookings"), icon: Calendar },
    { to: "/app/profile", label: t("profile"), icon: User },
  ];

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={{
        background: "var(--gradient-mesh), hsl(var(--cream-deep))",
        backgroundBlendMode: "soft-light",
      }}
    >
      <div
        className="relative h-[860px] w-full max-w-[420px] overflow-hidden rounded-[3rem] border-[10px] bg-background shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)]"
        style={{ borderColor: "hsl(var(--maroon))" }}
      >
        {/* notch */}
        <div className="pointer-events-none absolute left-1/2 top-2 z-40 h-5 w-24 -translate-x-1/2 rounded-full bg-maroon" />
        <div className="flex h-full flex-col">
          <div className="absolute left-3 right-3 top-3 z-30 flex items-center justify-between">
            <Link
              to="/"
              className="rounded-full glass px-3 py-1 text-[11px] font-semibold shadow-sm transition-transform hover:scale-105"
            >
              ← Site
            </Link>
            <LanguageSwitcher compact />
          </div>
          <div key={loc.pathname} className="flex-1 overflow-y-auto pb-24 animate-fade-up">
            <Outlet />
          </div>
          {!loc.pathname.startsWith("/app/ai") && (
            <Link
              to="/app/ai"
              aria-label="AI Assistant"
              className="absolute bottom-20 right-4 z-30 flex h-12 w-12 items-center justify-center rounded-full text-primary-foreground shadow-[0_10px_30px_-6px_color-mix(in_oklab,hsl(var(--saffron))_65%,transparent)] ring-2 ring-white/40 animate-shimmer"
              style={{ background: "var(--gradient-sunset)", backgroundSize: "200% 200%" }}
            >
              <Sparkles className="h-5 w-5 animate-flicker" />
              <span className="absolute -top-1 -right-1 rounded-full bg-maroon px-1.5 py-[1px] text-[8px] font-bold text-white">
                AI
              </span>
            </Link>
          )}
          <nav className="absolute bottom-3 left-3 right-3 flex justify-around rounded-2xl border border-border glass px-2 py-2 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.25)]">
            {TABS.map((tab) => {
              const active = tab.exact ? loc.pathname === tab.to : loc.pathname.startsWith(tab.to);
              return (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className={`relative flex flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5 text-[10px] font-semibold transition-all ${active ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  style={
                    active
                      ? { background: "var(--gradient-saffron)", boxShadow: "var(--shadow-warm)" }
                      : undefined
                  }
                >
                  <tab.icon
                    className={`h-5 w-5 transition-transform ${active ? "scale-110" : ""}`}
                  />
                  {tab.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
}
